import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/feature/SEOHead";
import { mangaList } from "@/mocks/manga";

export default function ChapterPage() {
  const { slug, chapterId } = useParams<{ slug: string; chapterId: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const manga = mangaList.find((m) => m.slug === slug);

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-800 mb-2">章节未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const currentChapter = manga.chapters.find((ch) => ch.id === chapterId) || manga.chapters[0];
  const currentIndex = manga.chapters.findIndex((ch) => ch.id === currentChapter.id);
  const prevChapter = currentIndex < manga.chapters.length - 1 ? manga.chapters[currentIndex + 1] : null;
  const nextChapter = currentIndex > 0 ? manga.chapters[currentIndex - 1] : null;
  const totalPages = currentChapter.pageCount;

  const pageTitle = `${manga.title} ${currentChapter.title} - 在线免费阅读 - 36漫画网`;
  const pageDesc = `36漫画网为您提供${manga.title}${currentChapter.title}高清在线免费阅读，无删减完整版，画质清晰，阅读体验极佳。`;
  const pageKeywords = `${manga.title},${currentChapter.title},漫画阅读,在线免费阅读,36漫画网`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${manga.title} - ${currentChapter.title}`,
    "author": { "@type": "Person", "name": manga.author },
    "datePublished": currentChapter.updateDate,
    "description": pageDesc,
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://www.36manhua.com/manga/${manga.slug}/chapter/${currentChapter.id}`}
        ogType="article"
        ogImage={manga.coverImage}
        schemaJson={articleSchema}
      />
      <Navbar />

      <main className="pt-16 min-h-screen bg-foreground-950">
        <div className="bg-foreground-900 border-b border-background-50/5 sticky top-16 z-40">
          <div className="w-full px-4 md:px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to={`/manga/${manga.slug}`}
                className="text-background-50/60 hover:text-background-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
              </Link>
              <div className="min-w-0">
                <h1 className="text-sm font-medium text-background-50 truncate">
                  {manga.title}
                </h1>
                <p className="text-xs text-background-50/40 truncate">
                  {currentChapter.title}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 mr-3">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="w-7 h-7 flex items-center justify-center rounded bg-background-50/10 text-background-50/60 hover:bg-background-50/20 disabled:opacity-30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-line text-sm"></i>
                </button>
                <span className="text-xs text-background-50/60 whitespace-nowrap">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  className="w-7 h-7 flex items-center justify-center rounded bg-background-50/10 text-background-50/60 hover:bg-background-50/20 disabled:opacity-30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </button>
              </div>

              <button className="w-8 h-8 flex items-center justify-center rounded bg-background-50/10 text-background-50/50 hover:text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-settings-3-line text-sm"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[800px] mx-auto">
            <div className="space-y-4">
              <div className="rounded-lg overflow-hidden bg-foreground-800">
                <img
                  src="https://readdy.ai/api/search-image?query=black%20and%20white%20manga%20comic%20page%20in%20Chinese%20style%2C%20detailed%20line%20art%20with%20screen%20tones%2C%20dramatic%20romance%20scene%2C%20manga%20panel%20layout%20with%20speed%20lines%20and%20speech%20bubbles%2C%20clean%20artwork%2C%20professional%20manga%20illustration%2C%20high%20contrast%20black%20and%20white&width=800&height=1100&seq=manga-page-01&orientation=portrait"
                  alt={`${currentChapter.title} 第1页`}
                  className="w-full h-auto"
                />
              </div>
              <div className="rounded-lg overflow-hidden bg-foreground-800">
                <img
                  src="https://readdy.ai/api/search-image?query=black%20and%20white%20manga%20comic%20page%20Chinese%20style%2C%20detailed%20line%20art%20screen%20tones%2C%20romance%20couple%20scene%2C%20manga%20panel%20layout%20speed%20lines%20speech%20bubbles%2C%20clean%20professional%20illustration%2C%20high%20contrast&width=800&height=1100&seq=manga-page-02&orientation=portrait"
                  alt={`${currentChapter.title} 第2页`}
                  className="w-full h-auto"
                />
              </div>
              <div className="rounded-lg overflow-hidden bg-foreground-800">
                <img
                  src="https://readdy.ai/api/search-image?query=black%20white%20manga%20comic%20page%20Chinese%20style%2C%20detailed%20line%20art%20screen%20tones%2C%20school%20campus%20scene%2C%20manga%20panel%20layout%20speed%20lines%20speech%20bubbles%2C%20clean%20professional%20illustration%2C%20high%20contrast&width=800&height=1100&seq=manga-page-03&orientation=portrait"
                  alt={`${currentChapter.title} 第3页`}
                  className="w-full h-auto"
                />
              </div>
              <div className="rounded-lg overflow-hidden bg-foreground-800">
                <img
                  src="https://readdy.ai/api/search-image?query=black%20white%20manga%20comic%20page%20Chinese%20style%2C%20detailed%20line%20art%20screen%20tones%2C%20emotional%20dramatic%20scene%2C%20manga%20panel%20layout%20speed%20lines%20speech%20bubbles%2C%20clean%20professional%20illustration%2C%20high%20contrast&width=800&height=1100&seq=manga-page-04&orientation=portrait"
                  alt={`${currentChapter.title} 第4页`}
                  className="w-full h-auto"
                />
              </div>
              <div className="rounded-lg overflow-hidden bg-foreground-800">
                <img
                  src="https://readdy.ai/api/search-image?query=black%20white%20manga%20comic%20page%20Chinese%20style%2C%20detailed%20line%20art%20screen%20tones%2C%20cliffhanger%20ending%20scene%2C%20manga%20panel%20layout%20speed%20lines%20speech%20bubbles%2C%20clean%20professional%20illustration%2C%20high%20contrast&width=800&height=1100&seq=manga-page-05&orientation=portrait"
                  alt={`${currentChapter.title} 第5页`}
                  className="w-full h-auto"
                />
              </div>
            </div>
            <p className="text-center text-background-50/30 text-xs mt-4">
              使用页面导航按钮翻页阅读更多内容
            </p>
          </div>
        </div>

        <div className="bg-foreground-900 border-t border-background-50/5 sticky bottom-0 z-40">
          <div className="w-full px-4 md:px-6 py-3">
            <div className="max-w-[800px] mx-auto flex items-center justify-between">
              {prevChapter ? (
                <Link
                  to={`/manga/${manga.slug}/chapter/${prevChapter.id}`}
                  className="flex items-center gap-2 text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-line"></i>
                  <span className="hidden sm:inline">上一话</span>
                  <span className="sm:hidden">上一话</span>
                </Link>
              ) : (
                <span className="text-sm text-background-50/20 whitespace-nowrap">
                  <i className="ri-arrow-left-s-line"></i> 上一话
                </span>
              )}

              <Link
                to={`/manga/${manga.slug}`}
                className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-list-unordered mr-1"></i>
                目录
              </Link>

              {nextChapter ? (
                <Link
                  to={`/manga/${manga.slug}/chapter/${nextChapter.id}`}
                  className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                >
                  <span className="hidden sm:inline">下一话</span>
                  <span className="sm:hidden">下一话</span>
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              ) : (
                <span className="text-sm text-background-50/20 whitespace-nowrap">
                  下一话 <i className="ri-arrow-right-s-line"></i>
                </span>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}